import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { excavatorService } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

// Material UI components
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Stack,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Import our page container for consistent layout
import PageContainer from "../components/PageContainer";
import ContentSection from "../components/ContentSection";

const ExcavatorEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager } = useContext(AuthContext);
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    model: "",
    manufacturer: "",
    serialNumber: "",
    year: new Date().getFullYear().toString(),
    status: "operational",
    size: "",
    machineType: "",
    location: "",
    hours: "0",
    nextMaintenanceHours: "100",
    description: "",
    notes: "",
    lastUVVDate: "",
    nextUVVDate: "",
    uvvStatus: "unknown",
    lastMaintenanceDate: "",
  });

  const [loading, setLoading] = useState(true); // For initial data load
  const [saving, setSaving] = useState(false); // For form submission
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchExcavator = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await excavatorService.getById(id);
        const excavator = response.data;
        setFormData({
          model: excavator.model || "",
          manufacturer: excavator.manufacturer || "",
          serialNumber: excavator.serialNumber || "",
          year: excavator.year?.toString() || new Date().getFullYear().toString(),
          status: excavator.status || "operational",
          size: excavator.size?.toString() || "",
          machineType: excavator.machineType || "",
          location: excavator.location || "",
          hours: excavator.hours?.toString() || "0",
          nextMaintenanceHours: excavator.nextMaintenanceHours?.toString() || "100",
          description: excavator.description || "",
          notes: excavator.notes || "",
          lastUVVDate: excavator.lastUVVDate ? new Date(excavator.lastUVVDate).toISOString().split("T")[0] : "",
          nextUVVDate: excavator.nextUVVDate ? new Date(excavator.nextUVVDate).toISOString().split("T")[0] : "",
          uvvStatus: excavator.uvvStatus || "unknown",
          lastMaintenanceDate: excavator.lastMaintenanceDate ? new Date(excavator.lastMaintenanceDate).toISOString().split("T")[0] : "",
        });
      } catch (err) {
        console.error("Error fetching excavator:", err);
        setError(t("failedToLoadExcavator") || "Failed to load excavator information.");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchExcavator();
  }, [id, t]);

  const validateField = (name, value) => {
    let fieldError = "";
    // Basic validation (can be expanded)
    switch (name) {
      case "manufacturer":
      case "model":
      case "serialNumber":
        if (!value.trim()) fieldError = t(`${name}Required`) || `${name} is required.`;
        break;
      case "year":
        if (!value) fieldError = t("yearRequired") || "Year is required.";
        else if (isNaN(parseInt(value)) || parseInt(value) < 1900 || parseInt(value) > new Date().getFullYear() + 1) {
            fieldError = t("invalidYear") || "Invalid year.";
        }
        break;
      case "hours":
      case "nextMaintenanceHours":
        if (value && (isNaN(parseInt(value)) || parseInt(value) < 0)) {
            fieldError = t("invalidHours") || "Hours must be a positive number.";
        }
        break;
      case "size":
         if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
            fieldError = t("invalidSize") || "Size must be a positive number.";
        }
        break;
      default:
        break;
    }
    return fieldError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
     if (validationErrors[name]) {
      setValidationErrors(prev => ({...prev, [name]: ''}));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
        const errorMsg = validateField(key, formData[key]);
        if (errorMsg) newErrors[key] = errorMsg;
    });
    if (formData.hours && formData.nextMaintenanceHours && parseInt(formData.nextMaintenanceHours) < parseInt(formData.hours)) {
        newErrors.nextMaintenanceHours = t("nextMaintenanceHoursError") || "Next maintenance hours must be greater than or equal to current hours.";
    }
    if (formData.lastUVVDate && formData.nextUVVDate && new Date(formData.nextUVVDate) < new Date(formData.lastUVVDate)) {
        newErrors.nextUVVDate = t("nextUVVError") || "Next UVV date must be after last UVV date.";
    }
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isManager()) {
      setError(t("permissionDeniedEditExcavator") || "You don't have permission to edit excavators.");
      return;
    }
    if (!validateForm()) return;

    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const excavatorData = {
        ...formData,
        year: parseInt(formData.year),
        hours: formData.hours ? parseInt(formData.hours) : 0,
        nextMaintenanceHours: formData.nextMaintenanceHours ? parseInt(formData.nextMaintenanceHours) : undefined,
        size: formData.size ? parseFloat(formData.size) : undefined,
        // Ensure dates are not empty strings before sending
        lastUVVDate: formData.lastUVVDate || undefined,
        nextUVVDate: formData.nextUVVDate || undefined,
        lastMaintenanceDate: formData.lastMaintenanceDate || undefined,
      };
      await excavatorService.update(id, excavatorData);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/excavators/${id}`);
      }, 1500);
    } catch (err) {
      console.error("Error updating excavator:", err);
      setError(err.response?.data?.message || err.message || t("failedToUpdateExcavator"));
    } finally {
      setSaving(false);
    }
  };

  const commonTextFieldProps = (name, type = "text") => ({
    name: name,
    type: type,
    value: formData[name],
    onChange: handleChange,
    fullWidth: true,
    variant: "outlined",
    margin: "normal",
    error: !!validationErrors[name],
    helperText: validationErrors[name] || "",
    disabled: saving || success,
    InputLabelProps: type === "date" ? { shrink: true } : {},
  });


  if (loading) {
    return (
      <PageContainer title={t("loading")}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t("editExcavator")}>
      <ContentSection
        title={`${t("editExcavator")} - ${formData.manufacturer} ${formData.model}`}
        icon={<EditIcon />}
        actions={
          <Button
            variant="outlined"
            onClick={() => navigate(`/excavators/${id}`)} // Navigate back to detail view
            startIcon={<ArrowBackIcon />}
            disabled={saving}
          >
            {t("backToDetail")}
          </Button>
        }
      >
        <Paper component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 3 } }} elevation={0}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{t("excavatorUpdatedSuccess")}</Alert>}

          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12}><Typography variant="h6" gutterBottom>{t("basicInformation")}</Typography></Grid>
            <Grid item xs={12} sm={6}><TextField label={`${t("manufacturer")}*`} {...commonTextFieldProps("manufacturer")} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={`${t("model")}*`} {...commonTextFieldProps("model")} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={`${t("serialNumber")}*`} {...commonTextFieldProps("serialNumber")} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={`${t("year")}*`} type="number" {...commonTextFieldProps("year")} InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() + 1 } }} /></Grid>

            {/* Operational Details */}
            <Grid item xs={12}><Divider sx={{ my: 2 }}><Typography variant="overline">{t("operationalDetails")}</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" error={!!validationErrors.status}>
                <InputLabel id="status-label">{t("status")}</InputLabel>
                <Select labelId="status-label" label={t("status")} {...commonTextFieldProps("status")}>
                  <MenuItem value="operational">{t("operational")}</MenuItem>
                  <MenuItem value="in maintenance">{t("underMaintenance")}</MenuItem>
                  <MenuItem value="in repair">{t("inRepair")}</MenuItem>
                  <MenuItem value="out of service">{t("outOfService")}</MenuItem>
                </Select>
                {validationErrors.status && <FormHelperText>{validationErrors.status}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField label={t("sizeTons")} type="number" {...commonTextFieldProps("size")} InputProps={{ inputProps: { step: "0.1", min: "0" } }} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" error={!!validationErrors.machineType}>
                <InputLabel id="machineType-label">{t("machineType")}</InputLabel>
                <Select labelId="machineType-label" label={t("machineType")} {...commonTextFieldProps("machineType")}>
                    <MenuItem value="">{t("selectType")}</MenuItem>
                    <MenuItem value="Crawler Excavator">{t("crawlerExcavator")}</MenuItem>
                    <MenuItem value="Wheeled Excavator">{t("wheeledExcavator")}</MenuItem>
                    <MenuItem value="Frontloader">{t("frontloader")}</MenuItem>
                    <MenuItem value="Backhoe Loader">{t("backhoeLoader")}</MenuItem>
                    <MenuItem value="Skid-steer Loader">{t("skidSteerLoader")}</MenuItem>
                    <MenuItem value="Bulldozer">{t("bulldozer")}</MenuItem>
                    <MenuItem value="Grader">{t("grader")}</MenuItem>
                    <MenuItem value="Other">{t("other")}</MenuItem>
                </Select>
                {validationErrors.machineType && <FormHelperText>{validationErrors.machineType}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField label={t("currentHours")} type="number" {...commonTextFieldProps("hours")} InputProps={{ inputProps: { min: "0" } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={t("location")} {...commonTextFieldProps("location")} /></Grid>

            {/* Maintenance Info */}
            <Grid item xs={12}><Divider sx={{ my: 2 }}><Typography variant="overline">{t("maintenanceInformation")}</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}><TextField label={t("nextMaintenanceHours")} type="number" {...commonTextFieldProps("nextMaintenanceHours")} InputProps={{ inputProps: { min: formData.hours } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={t("lastMaintenanceDate")} {...commonTextFieldProps("lastMaintenanceDate", "date")} /></Grid>

            {/* UVV/SP Info */}
            <Grid item xs={12}><Divider sx={{ my: 2 }}><Typography variant="overline">{t("uvvSpInformation")}</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}><TextField label={t("lastUVVDate")} {...commonTextFieldProps("lastUVVDate", "date")} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={t("nextUVVDate")} {...commonTextFieldProps("nextUVVDate", "date")} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" error={!!validationErrors.uvvStatus}>
                <InputLabel id="uvvStatus-label">{t("uvvSpStatus")}</InputLabel>
                <Select labelId="uvvStatus-label" label={t("uvvSpStatus")} {...commonTextFieldProps("uvvStatus")}>
                  <MenuItem value="upToDate">{t("upToDate")}</MenuItem>
                  <MenuItem value="due">{t("dueSoon")}</MenuItem>
                  <MenuItem value="overdue">{t("overdue")}</MenuItem>
                  <MenuItem value="unknown">{t("unknown")}</MenuItem>
                </Select>
                {validationErrors.uvvStatus && <FormHelperText>{validationErrors.uvvStatus}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Description & Notes */}
            <Grid item xs={12}><Divider sx={{ my: 2 }}><Typography variant="overline">{t("additionalInformation")}</Typography></Divider></Grid>
            <Grid item xs={12}><TextField label={t("description")} multiline rows={3} {...commonTextFieldProps("description")} /></Grid>
            <Grid item xs={12}><TextField label={t("notes")} multiline rows={2} {...commonTextFieldProps("notes")} /></Grid>
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
            <Button variant="outlined" color="secondary" onClick={() => navigate(`/excavators/${id}`)} disabled={saving}>
              {t("cancel")}
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={saving || success} startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}>
              {saving ? t("saving") : success ? t("saved") : t("updateExcavator")}
            </Button>
          </Stack>
        </Paper>
      </ContentSection>
    </PageContainer>
  );
};

export default ExcavatorEdit;
